import {
  action$,
  combineReducers,
  ofType,
  streamReducer,
} from '@ardoq/rxbeach';
import { combineLatest } from 'rxjs';
import { context$ } from 'streams/context/context$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  APIComponentAttributes,
  APIFieldType,
  ArdoqId,
  ViewIds,
} from '@ardoq/api-types';
import { getComponentCssColors } from 'utils/modelCssManager/getCssColors';
import { notifyFilterColorChanged } from 'streams/filters/FilterActions';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { componentInterface } from '@ardoq/component-interface';
import { isReferenceIdIncludedInContextByFilter } from '@ardoq/reference-interface';
import { fieldInterface } from '@ardoq/field-interface';
import type {
  CalculationFn,
  ComponentDecoratorImageProps,
  ComponentsReducerOptions,
  ComponentsToDisplay,
  LegendProps,
  TreemapData,
  TreemapDataDisplayOptions,
  ViewModel,
  ViewSettings,
} from './types';
import { WorkspaceField } from './types';
import { getActiveField, isByCustomField } from './helpers';
import { representationDataToEntityIconProps } from '@ardoq/basic-components';
import { logWarn } from '@ardoq/logging';
import { EMPTY_DISPLAY_OPTIONS, MISSING_FIELD_WARN_MESSAGE } from './constants';
import { settingsBarConsts } from '@ardoq/settings-bar';
import { getComponentTypesInfo, HasViewInstanceId } from '@ardoq/graph';
import { graphModel$ } from 'modelInterface/graphModel$';
import { GraphModelMap, GraphModelShape } from '@ardoq/data-model';
import { components$, ComponentsState } from 'streams/components/components$';
import { componentOperations, toIds } from '@ardoq/core';
import { fields$ } from 'streams/fields/fields$';
import { startWith } from 'rxjs/operators';
import { SHOW_ALL_COMPONENTS, SizeBasedOn } from 'views/treemapConsts';
import { ExtractStreamShape } from 'tabview/types';
import { colors } from '@ardoq/design-tokens';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { permissionsOperations } from '@ardoq/access-control';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { formatNumber } from '@ardoq/locale';

const { getRepresentationData, isIncludedInContextByFilter } =
  componentInterface;

const EMPTY_STATE: ViewModel = {
  data: {
    id: null,
    label: '',
    value: 0,
    formattedValue: '0',
    displayOptions: EMPTY_DISPLAY_OPTIONS,
  },
  settings: {
    sizeBasedOn: SizeBasedOn.CHILD_COUNT,
    maxLimitValue: SHOW_ALL_COMPONENTS,
  },
  legend: {
    isActive: false,
    componentTypes: [],
  },
  workspaceFields: [],
  fieldsInUse: new Set<ArdoqId>(),
  hasIncomingReferences: false,
  hasOutgoingReferences: false,
  hasComponentsAvailable: false,
  permissionContext: permissionsOperations.createEmptyPermissionContext(),
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
};

const checkHasReferences = (
  componentId: ArdoqId,
  graphModelMap: GraphModelMap
) =>
  Boolean(
    graphModelMap
      .get(componentId)
      ?.some(({ referenceId }) =>
        isReferenceIdIncludedInContextByFilter(referenceId)
      )
  );

const getReferenceCount = (
  component: APIComponentAttributes,
  graphModelMap: GraphModelMap,
  components: ComponentsState
) => {
  const subtree = componentOperations.getFlatHierarchy(
    [component],
    components.childrenByParent
  );

  return subtree.reduce((acc, descendant) => {
    const referenceCount =
      graphModelMap
        .get(descendant._id)
        ?.filter(({ referenceId }) =>
          isReferenceIdIncludedInContextByFilter(referenceId)
        ).length || 0;

    return acc + referenceCount;
  }, 0);
};

const createCalculationFn = (
  settings: ViewSettings,
  graphModel: GraphModelShape,
  components: ComponentsState
): CalculationFn => {
  if (isByCustomField(settings.sizeBasedOn)) {
    return component => {
      const selectedField = settings.sizeBasedOn;

      if (!selectedField) {
        logWarn(Error(MISSING_FIELD_WARN_MESSAGE), null, {
          viewId: ViewIds.TREEMAP,
          settings,
        });
        return undefined;
      }

      const subtree = componentOperations.getFlatHierarchy(
        [component],
        components.childrenByParent
      );
      return subtree.reduce((acc, descendant) => {
        const value = descendant[selectedField] ?? null;

        if (typeof value === 'number') {
          return acc + value;
        }

        return acc;
      }, 0);
    };
  }

  switch (settings.sizeBasedOn) {
    case SizeBasedOn.CHILD_COUNT:
      return component =>
        componentOperations.getFlatHierarchy(
          [component],
          components.childrenByParent
        ).length - 1;
    case SizeBasedOn.INCOMING_LINKS:
      return component =>
        getReferenceCount(component, graphModel.targetMap, components);
    case SizeBasedOn.OUTGOING_LINKS:
      return component =>
        getReferenceCount(component, graphModel.sourceMap, components);
    default:
      return _component => 0;
  }
};

const getTopChildren = (limit: number, components: TreemapData[]) => {
  if (limit <= 0 || limit >= components.length) return components;
  return components.sort((a, b) => b.value - a.value).slice(0, limit);
};

const validateSettings = (
  settings: ViewSettings,
  workspaceFields: WorkspaceField[]
) => {
  const isCustomField = isByCustomField(settings.sizeBasedOn);
  const selectedField = isCustomField ? settings.sizeBasedOn : null;

  const selectedInCurrentWorkspace = workspaceFields.some(
    ({ name }) => name === selectedField
  );

  const isFieldNotFromCurrentWorkspace =
    isCustomField && (!selectedField || !selectedInCurrentWorkspace);

  if (isFieldNotFromCurrentWorkspace) {
    return {
      ...settings,
      sizeBasedOn: SizeBasedOn.CHILD_COUNT,
    };
  }
  return settings;
};

const getComponentDecoratorImageProps = (
  componentId: string
): ComponentDecoratorImageProps | undefined => {
  const representationData = getRepresentationData(componentId);
  if (!representationData) return undefined;
  return representationDataToEntityIconProps(representationData);
};

const getComponentDisplayOptions = (
  componentId: ArdoqId,
  currentComponentId: string
): TreemapDataDisplayOptions => {
  const componentColors = getComponentCssColors(componentId, {
    useAsBackgroundStyle: true,
  });
  const componentDecoratorImageProps =
    getComponentDecoratorImageProps(componentId) ?? {};
  const isSelected = currentComponentId === componentId;
  return {
    componentDecoratorImageProps,
    color: isSelected ? colors.blue600 : componentColors?.stroke,
    backgroundColor: isSelected ? colors.blue50 : componentColors?.fill,
    isSelected: isSelected,
  };
};

const reduceComponent =
  ({ getWeight, context, formatValue }: ComponentsReducerOptions) =>
  (acc: TreemapData[], component: APIComponentAttributes) => {
    if (!isIncludedInContextByFilter(component)) return acc;
    const weight = getWeight(component);
    if (!weight) return acc;
    const displayOptions = getComponentDisplayOptions(
      component._id,
      context.componentId
    );
    return [
      ...acc,
      {
        id: component._id,
        label: component.name,
        value: weight,
        formattedValue: formatValue(weight),
        displayOptions,
      },
    ];
  };

const getComponentsToDisplay = (
  componentId: string,
  components: ComponentsState
): ComponentsToDisplay | undefined => {
  const children = components.childrenByParent.get(componentId) ?? [];
  if (children.length) {
    return {
      components: [...children].reverse(),
      containerId: componentId,
    };
  }
  const parentId = components.byId[componentId]?.parent ?? null;
  if (parentId) return getComponentsToDisplay(parentId, components);
  return undefined;
};

const getContainerData = (
  containerId: ArdoqId | undefined,
  workspaceId: ArdoqId,
  children: TreemapData[],
  reduceComponentFn: (
    acc: TreemapData[],
    component: APIComponentAttributes
  ) => TreemapData[],
  components: ComponentsState
) => {
  const doNotShowContainer = !containerId && children.length > 0;

  if (doNotShowContainer || !containerId) return EMPTY_STATE.data;

  const containerData = components.byId[containerId];

  if (!containerData) return EMPTY_STATE.data;

  const [data] = [containerData].reduce(reduceComponentFn, []);

  if (!data) {
    return EMPTY_STATE.data;
  }

  return {
    workspaceId,
    ...data,
  };
};

const getLegend = (
  settings: ViewSettings,
  children: TreemapData[]
): LegendProps => {
  const isLegendActive = settings[settingsBarConsts.IS_LEGEND_ACTIVE] ?? false;
  const { legendComponentTypes } = getComponentTypesInfo(
    children.map(({ id }) => id!)
  );

  return {
    isActive: isLegendActive,
    componentTypes: legendComponentTypes,
  };
};

export const getViewModel$ = ({ viewInstanceId }: HasViewInstanceId) => {
  const treemapContext$ = combineLatest([
    getWorkspaceModeViewSettings$(ViewIds.TREEMAP, viewInstanceId),
    context$,
    components$,
    graphModel$,
    currentUserPermissionContext$,
    i18nLocale$,
    locale$,
    fields$,
    workspaceAndModel$,
    action$.pipe(ofType(notifyFilterColorChanged), startWith(null)),
  ]);

  const reset = (
    _: ViewModel,
    [
      settings,
      context,
      components,
      graphModel,
      permissionContext,
      i18nLocale,
      locale,
    ]: ExtractStreamShape<typeof treemapContext$>
  ): ViewModel => {
    const workspaceFields = fieldInterface
      .getFieldsOfWorkspace(context.workspaceId, [APIFieldType.NUMBER])
      .map(({ _id, name, label }) => ({
        _id,
        name,
        label: label || name,
        workspaceId: context.workspaceId,
      }));

    const { components: visibleComponents, containerId } =
      getComponentsToDisplay(context.componentId, components) ?? {};

    const rootComponents =
      visibleComponents ??
      componentOperations.getRootComponents(
        context.workspaceId,
        components.models
      );

    const componentTree = componentOperations.getFlatHierarchy(
      rootComponents,
      components.childrenByParent
    );
    const componentTreeIds = toIds(componentTree);

    const hasIncomingReferences = componentTreeIds.some(componentId =>
      checkHasReferences(componentId, graphModel.targetMap)
    );

    const hasOutgoingReferences = componentTreeIds.some(componentId =>
      checkHasReferences(componentId, graphModel.sourceMap)
    );

    const safeSettings = validateSettings(settings, workspaceFields);
    const getWeight = createCalculationFn(safeSettings, graphModel, components);

    const activeField = getActiveField(
      safeSettings.sizeBasedOn,
      workspaceFields
    );

    const activeFieldNumberFormat = activeField
      ? (fieldInterface.getNumberFormatByName(activeField.name) ?? undefined)
      : undefined;

    const formatValue = (value: number) =>
      formatNumber(value, activeFieldNumberFormat, locale);

    const reduceComponentFn = reduceComponent({
      getWeight,
      context,
      formatValue,
    });

    const children = rootComponents
      .reduce<TreemapData[]>(reduceComponentFn, [])
      .filter(({ value }) => value > 0);

    const containerData = getContainerData(
      containerId,
      context.workspaceId,
      children,
      reduceComponentFn,
      components
    );

    const topChildren = getTopChildren(safeSettings.maxLimitValue, children);

    const fieldsInUse = new Set<string>(
      workspaceFields.reduce((acc: string[], field) => {
        const isFieldInUse = componentTree.some(component =>
          Boolean(component[field.name])
        );

        return isFieldInUse ? [...acc, field.name] : acc;
      }, [])
    );

    const activeFieldLabel = activeField
      ? (fieldInterface.getLabel(activeField._id) ?? undefined)
      : undefined;

    const hasComponentsAvailable = !!rootComponents.length;

    const legend = getLegend(safeSettings, topChildren);

    return {
      data: {
        ...containerData,
        children: topChildren,
      },
      settings: safeSettings,
      workspaceFields,
      fieldsInUse,
      legend,
      hasIncomingReferences,
      hasOutgoingReferences,
      hasComponentsAvailable,
      activeFieldLabel,
      permissionContext,
      i18nLocale,
    };
  };

  const getContextReducers = streamReducer(treemapContext$, reset);

  return action$.pipe(
    combineReducers<ViewModel>(EMPTY_STATE, [getContextReducers])
  );
};
